import * as React from 'react';
import {useEffect} from 'react';
import {Autocomplete, Grid, Paper, Typography, Stack, useTheme, useMediaQuery, Divider} from '@mui/material';
import 'dayjs/locale/tr';
import dayjs from "dayjs";
import {DatePicker} from "@mui/x-date-pickers";
import Box from "@mui/material/Box";
import logbookApi from "../../../api/staffApi/logbookApi";
import {IEtkinlik, IKalem, IKisi, IMesaiKayit} from "../../../constants/types/logbook";
import {useSelector} from "../../../redux/store";
import IconButton from "@mui/material/IconButton";
import {AccountCircle, ArrowLeft, ArrowRight, Event, CalendarMonth, FilterList, Engineering} from "@mui/icons-material";
import TextField from "@mui/material/TextField";
import hasPermission from "../../../redux/hasPermission";
import GunSecimiDialog from "./GunSecimiDialog";

const InitData = {
    data: [],
    guncellenecekListe: [],
    guncellenecekIdListe: [],
    silinecekIdListe: [],
    eklenenListe: []
}

const Takvim = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
    const sabitler = useSelector(state => state.app.sabitler);
    const kisiler = (sabitler?.kisiler as IKisi[]) ?? [];

    const [seciliAy, setSeciliAy] = React.useState<dayjs.Dayjs>(dayjs());
    const [seciliTarih, setSeciliTarih] = React.useState<dayjs.Dayjs | null>(null);
    const [seciliTarihCalisanlar, setSeciliTarihCalisanlar] = React.useState<IKalem<IMesaiKayit>>(InitData);
    const [seciliTarihEtkinlikler, setSeciliTarihEtkinlikler] = React.useState<IKalem<IEtkinlik>>(InitData);
    const [kisiFiltre, setKisiFiltre] = React.useState<IKisi | null>(null);
    const [seciliDonemKayitlar, setseciliDonemKayitlar] = React.useState<Record<string, {
        mesailer: IMesaiKayit[],
        etkinlikler: IEtkinlik[]
    }>>({});
    
    const mevcutAyKisileri = React.useMemo(() => {
        const uniqueIds = new Set<number>();
        Object.entries(seciliDonemKayitlar).forEach(([dateStr, data]) => {
            const date = dayjs(dateStr, "DD-MM-YYYY");
            if (date.month() === seciliAy.month()) {
                data.mesailer.forEach(m => {
                    if (m.kisi) uniqueIds.add(m.kisi);
                });
            }
        });
        return kisiler.filter(k => uniqueIds.has(k.id));
    }, [seciliDonemKayitlar, seciliAy, kisiler]);
    
    const takvimDuzenleYetki = hasPermission("galleon_logbook.takvimDuzenle");
    const startOfMonth = seciliAy.startOf('month')
    const enOfMonth = seciliAy.endOf('month')
    const firstDayNumber = ((startOfMonth?.day() < 1) ? 6 : startOfMonth?.day() - 1)
    const lastDayNumber = ((enOfMonth?.day() < 1) ? 0 : 7 - enOfMonth?.day())

    const startOfCalendar = startOfMonth.subtract(firstDayNumber, 'day')
    const endOfCalendar = enOfMonth.add(lastDayNumber, 'day')

    const takvimVerileriGetir = () => {
        logbookApi.getTakvimKayitlari({
            baslangicTarih: startOfCalendar.format("DD-MM-YYYY"),
            bitisTarih: endOfCalendar.format("DD-MM-YYYY")
        }).then((res: { mesailer: IMesaiKayit[], etkinlikler: IEtkinlik[] }) => {
            const kayitlar: Record<string, { mesailer: IMesaiKayit[], etkinlikler: IEtkinlik[] }> = {}
            res?.mesailer?.forEach(kayit => {
                if (!kayitlar[kayit.tarih]) {
                    kayitlar[kayit.tarih] = {mesailer: [kayit], etkinlikler: []}
                } else {
                    kayitlar[kayit.tarih].mesailer.push(kayit)
                }
            })
            res?.etkinlikler?.forEach((kayit: any) => {
                if (!kayitlar[kayit.etkinlik_tarihi]) {
                    kayitlar[kayit.etkinlik_tarihi] = {mesailer: [], etkinlikler: [kayit]}
                } else {
                    kayitlar[kayit.etkinlik_tarihi].etkinlikler.push(kayit)
                }
            })
            setseciliDonemKayitlar(kayitlar)

            if (seciliTarih) {
                const dateStr = seciliTarih.format("DD-MM-YYYY");
                setSeciliTarihCalisanlar({
                    ...InitData,
                    data: [...kayitlar[dateStr]?.mesailer ?? []]
                });
                setSeciliTarihEtkinlikler({
                    ...InitData,
                    data: [...kayitlar[dateStr]?.etkinlikler ?? []]
                });
            }
        })
    }
    
    useEffect(() => {
        takvimVerileriGetir();
    }, [seciliAy]);

    const tarihler = []
    let dt = startOfCalendar
    while (dt <= endOfCalendar) {
        tarihler.push(dt);
        dt = dt.add(1, "day")
    }

    const gunler = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"]

    return (
        <Box sx={{ 
            width: { xs: '100vw', md: '90vw' }, 
            maxWidth: (theme) => theme.breakpoints.values.xl, 
            mx: 'auto',
            p: { xs: 0.5, sm: 2 } 
        }}>
            {!!seciliTarih && <GunSecimiDialog
                onClose={() => {
                    setSeciliTarih(null)
                    setSeciliTarihCalisanlar(InitData)
                    setSeciliTarihEtkinlikler(InitData)
                }}
                takvimVerileriGetir={takvimVerileriGetir}
                seciliTarih={seciliTarih}
                seciliTarihCalisanlar={seciliTarihCalisanlar}
                setSeciliTarihCalisanlar={setSeciliTarihCalisanlar}
                seciliTarihEtkinlikler={seciliTarihEtkinlikler}
                setSeciliTarihEtkinlikler={setSeciliTarihEtkinlikler}
                kisiler={kisiler}
            />}

            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FilterList color="action" fontSize="small" />
                            <Autocomplete
                                size="small"
                                fullWidth
                                options={mevcutAyKisileri}
                                getOptionLabel={(kisi) => kisi?.rumuz || ''}
                                value={kisiFiltre}
                                onChange={(_, v) => setKisiFiltre(v)}
                                renderInput={(params) => <TextField {...params} label="Filtrele" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'center', sm: 'flex-end' }}>
                            <IconButton onClick={() => setSeciliAy(seciliAy.subtract(1, "month"))} sx={{ bgcolor: 'grey.100', p: { xs: 0.5, sm: 1 } }}><ArrowLeft /></IconButton>
                            <DatePicker
                                value={seciliAy}
                                onChange={(date) => date && setSeciliAy(dayjs(date).set("day", 1))}
                                views={['year', 'month']}
                                slotProps={{ textField: { size: 'small', sx: { width: { xs: 120, sm: 150 }, '& .MuiOutlinedInput-root': { borderRadius: '10px' } } } }}
                            />
                            <IconButton onClick={() => setSeciliAy(seciliAy.add(1, "month"))} sx={{ bgcolor: 'grey.100', p: { xs: 0.5, sm: 1 } }}><ArrowRight /></IconButton>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ 
                bgcolor: 'grey.100', 
                p: 0.5, 
                borderRadius: '12px',
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: { xs: 0.3, sm: 0.5 },
                width: '100%',
                overflow: 'hidden'
            }}>
                {gunler.map((gun) => (
                    <Box key={gun} sx={{ pb: { xs: 0.5, sm: 1 } }}>
                        <Typography 
                            variant="overline" 
                            align="center" 
                            display="block" 
                            sx={{ 
                                fontWeight: 800, 
                                color: 'text.secondary',
                                fontSize: { xs: '0.55rem', sm: '0.75rem' },
                                lineHeight: 1.2
                            }}
                        >
                            {isMobile ? gun.slice(0, 2) : gun}
                        </Typography>
                    </Box>
                ))}

                {tarihler.map((tarih) => {
                    const dateStr = tarih.format("DD-MM-YYYY");
                    const isOtherMonth = tarih.month() !== seciliAy.month();
                    const isToday = tarih.isSame(dayjs(), 'day');
                    const dayData = seciliDonemKayitlar[dateStr];

                    return (
                        <Paper
                            key={dateStr}
                            elevation={0}
                            sx={{
                                minHeight: { xs: 70, sm: 120 },
                                p: { xs: 0.3, sm: 1 },
                                borderRadius: '8px',
                                border: isToday ? '2px solid' : '1px solid',
                                borderColor: isToday ? 'primary.main' : 'rgba(0,0,0,0.05)',
                                bgcolor: isOtherMonth ? 'grey.50' : 'white',
                                opacity: isOtherMonth ? 0.6 : 1,
                                transition: 'all 0.2s',
                                cursor: takvimDuzenleYetki ? 'pointer' : 'default',
                                '&:hover': takvimDuzenleYetki ? { transform: isSmUp ? 'scale(1.02)' : 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 1 } : {},
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                            onClick={() => {
                                if (!takvimDuzenleYetki) return;
                                setSeciliTarih(tarih)
                                setSeciliTarihCalisanlar({...InitData, data: [...dayData?.mesailer ?? []]})
                                setSeciliTarihEtkinlikler({...InitData, data: [...dayData?.etkinlikler ?? []]})
                            }}
                        >
                            <Box sx={{ mb: { xs: 0.2, sm: 1 }, display: 'flex', justifyContent: 'center', sm: { justifyContent: 'flex-start' } }}>
                                <Typography 
                                    variant={isMobile ? "body2" : "h6"} 
                                    fontWeight={isToday ? 900 : 700} 
                                    color={isToday ? 'primary.main' : 'text.primary'} 
                                    sx={{ lineHeight: 1, fontSize: { xs: '0.75rem', sm: '1.25rem' } }}
                                >
                                    {tarih.date()}
                                </Typography>
                            </Box>

                            <Stack spacing={0.2} sx={{ flexGrow: 1, width: '100%', overflow: 'hidden' }}>
                                {dayData?.mesailer?.map((kayit, i) => {
                                    const kisi = kisiler.find(k => k.id === kayit.kisi);
                                    const isFiltered = kisiFiltre && kisiFiltre.id !== kayit.kisi;
                                    if (isFiltered) return null;
                                    
                                    return (
                                        <Box key={i} sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.3, 
                                            bgcolor: 'rgba(0,0,0,0.03)', 
                                            p: 0.1, 
                                            borderRadius: '2px',
                                            width: '100%',
                                            overflow: 'hidden'
                                        }}>
                                            <Engineering sx={{ fontSize: { xs: 7, sm: 12 }, color: kisi?.renk || 'primary.main', flexShrink: 0 }} />
                                            <Typography 
                                                variant="caption" 
                                                noWrap 
                                                fontWeight={700} 
                                                color="text.primary" 
                                                sx={{ fontSize: { xs: '0.45rem', sm: '0.65rem' }, minWidth: 0 }}
                                            >
                                                {isMobile ? kisi?.rumuz?.slice(0, 3) : `${kayit.baslangic_saat.slice(0, 5)} ${kisi?.rumuz}`}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                {dayData?.etkinlikler?.map((kayit, i) => (
                                    <Box key={i} sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.3, 
                                        bgcolor: 'secondary.light', 
                                        p: 0.1, 
                                        borderRadius: '2px',
                                        width: '100%',
                                        overflow: 'hidden'
                                    }}>
                                        <Event sx={{ fontSize: { xs: 7, sm: 12 }, color: 'secondary.main', flexShrink: 0 }} />
                                        <Typography 
                                            variant="caption" 
                                            noWrap 
                                            fontWeight={700} 
                                            color="secondary.dark" 
                                            sx={{ fontSize: { xs: '0.45rem', sm: '0.65rem' }, minWidth: 0 }}
                                        >
                                            {kayit.isim}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Takvim;
